import { ReactElement, useState } from 'react';

import { UploadResult } from 'firebase/storage';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import League from '../../types/League';
import Org from '../../types/Org';
import DefaultEventSettingsEditor from '../common/DefaultEventSettingsEditor';

import LeagueCardPreview from './LeagueCardPreview';
import LeagueGeneralSettingsEditor from './LeagueGeneralSettingsEditor';
import { LeaguePreviewElement, LeagueEditorSection, LEAGUE_ELEMENT_TO_EDITOR } from './leaguePreviewTypes';

export interface LeagueReviewAndFinalizeProps {
  league: League;
  org?: Org;
  isNew?: boolean;
  isEditing?: boolean;
  onLeagueChange: (league: League) => void;
  onEditingClose?: () => void;
  onEditingOpen?: () => void;
  onImageUpdated?: (snapshot: UploadResult | null) => void;
  onFileSelected?: (file: File | null) => void;
  onOrgChange?: (orgPair: { orgId: string; orgName: string }) => void;
}

export default function LeagueReviewAndFinalize(props: LeagueReviewAndFinalizeProps): ReactElement {
  const {
    league,
    org,
    isNew = true,
    isEditing = false,
    onLeagueChange,
    onEditingClose,
    onEditingOpen,
    onImageUpdated,
    onFileSelected,
    onOrgChange,
  } = props;

  const [expandedSection, setExpandedSection] = useState<LeagueEditorSection | false>('general');

  const handleAccordionChange = (panel: LeagueEditorSection) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const handleElementClick = (element: LeaguePreviewElement) => {
    const mapping = LEAGUE_ELEMENT_TO_EDITOR[element];

    // Open editing panel if not already open
    if (!isEditing && onEditingOpen) {
      onEditingOpen();
    }

    // Expand the appropriate accordion section
    setExpandedSection(mapping.section);

    // Focus the field after a brief delay to allow accordion to expand
    if (mapping.field) {
      setTimeout(() => {
        const fieldElement = document.getElementById(mapping.field!);
        if (fieldElement) {
          fieldElement.focus();
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  const handleUpdate = <K extends keyof League>(field: K, value: League[K]) => {
    onLeagueChange({ ...league, [field]: value });
  };

  return (
    <Grid container spacing={3} sx={{ pt: 2, alignItems: 'stretch', overflow: 'hidden' }}>
      {/* Preview Card - Full width when not editing, left half when editing on md+ */}
      <Grid
        size={{ xs: 12, md: isEditing ? 5 : 12 }}
        sx={{
          transition: 'all 300ms ease-in-out',
        }}
      >
        <Stack spacing={2}>
          <Fade in timeout={400}>
            <Stack spacing={0.5} sx={{
              alignItems: "flex-start"
            }}>
              <Typography variant="body1" sx={{
                color: "text.secondary"
              }}>
                This is how the league will appear to members.
              </Typography>
              {!isEditing && onEditingOpen && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={onEditingOpen}
                >
                  Edit
                </Button>
              )}
            </Stack>
          </Fade>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: '27.75em', width: '100%' }}>
              <LeagueCardPreview
                league={league}
                variant="outlined"
                onElementClick={handleElementClick}
              />
            </Box>
          </Box>

          {/* "Add" links for missing optional elements */}
          {(!league.logo || !league.description?.trim()) && (
            <Stack direction="row" spacing={2} sx={{
              justifyContent: "center"
            }}>
              {!league.logo && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => handleElementClick('logo')}
                >
                  + Add logo
                </Link>
              )}
              {!league.description?.trim() && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => handleElementClick('description')}
                >
                  + Add description
                </Link>
              )}
            </Stack>
          )}
        </Stack>
      </Grid>
      {/* Edit Sections - Always rendered for animation, visibility controlled by Slide */}
      <Grid
        size={{ xs: 12, md: isEditing ? 7 : 0 }}
        sx={{
          display: 'flex',
          transition: 'all 300ms ease-in-out',
          overflow: 'hidden',
        }}
      >
        <Slide direction="left" in={isEditing} mountOnEnter unmountOnExit>
          <Box
            sx={{
              borderLeft: 2,
              borderColor: 'divider',
              pl: 2,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Tap a section to make changes.
                </Typography>
                {onEditingClose && (
                  <IconButton onClick={onEditingClose} size="small" aria-label="Close edit panel">
                    <CloseIcon />
                  </IconButton>
                )}
              </Stack>

              {/* General Settings */}
              <Accordion
                expanded={expandedSection === 'general'}
                onChange={handleAccordionChange('general')}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">General Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LeagueGeneralSettingsEditor
                    league={league}
                    isNew={isNew}
                    onUpdate={handleUpdate}
                    onOrgChange={onOrgChange}
                    onImageUpdated={onImageUpdated}
                    onFileSelected={onFileSelected}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Event Defaults */}
              <Accordion
                expanded={expandedSection === 'eventDefaults'}
                onChange={handleAccordionChange('eventDefaults')}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Event Defaults</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 2
                    }}>
                    These defaults are used when creating new events. Leave blank to inherit from organization.
                  </Typography>
                  <DefaultEventSettingsEditor
                    settings={{
                      defaultPlayDays: league.defaultPlayDays,
                      defaultEventSignupOpensDaysPrior: league.defaultEventSignupOpensDaysPrior,
                      defaultEventSignupClosesDaysPrior: league.defaultEventSignupClosesDaysPrior,
                      defaultCoursePair: league.defaultCoursePair,
                      defaultStartTime: league.defaultStartTime,
                      defaultMaxPlayers: league.defaultMaxPlayers,
                      defaultNumTeamsInMatch: league.defaultNumTeamsInMatch,
                      defaultSignupPriority: league.defaultSignupPriority,
                      defaultGhinIndexSetDaysPrior: league.defaultGhinIndexSetDaysPrior,
                      defaultTeamSize: league.defaultTeamSize,
                      defaultTeeSheetGroupSize: league.defaultTeeSheetGroupSize,
                    }}
                    onChange={(field, value) => handleUpdate(field, value as League[typeof field])}
                    parentSettings={{
                      defaultPlayDays: org?.defaultPlayDays,
                      defaultEventSignupOpensDaysPrior: org?.defaultEventSignupOpensDaysPrior,
                      defaultEventSignupClosesDaysPrior: org?.defaultEventSignupClosesDaysPrior,
                      defaultCoursePair: org?.defaultCoursePair,
                      defaultStartTime: org?.defaultStartTime,
                      defaultMaxPlayers: org?.defaultMaxPlayers,
                      defaultNumTeamsInMatch: org?.defaultNumTeamsInMatch,
                      defaultSignupPriority: org?.defaultSignupPriority,
                      defaultGhinIndexSetDaysPrior: org?.defaultGhinIndexSetDaysPrior,
                      defaultTeamSize: org?.defaultTeamSize,
                      defaultTeeSheetGroupSize: org?.defaultTeeSheetGroupSize,
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Box>
        </Slide>
      </Grid>
    </Grid>
  );
}
